import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isEqual } from "lodash";

import { useStateSelector } from "utils/field";
import {
    collectComponentDirectionKeysByPath,
    defaultComponentFilter,
    defaultDirectionFilter,
    getAvailableDirectionFilterOptions,
    getComponentDirectionKey,
    getDefaultDirectionFilterForOptions,
    isDirectionFilterApplied,
    normalizeDirectionFilterValue,
    resetDirectionFilterAvailabilityTracking,
    setDirectionFilterTrackingModelId,
    syncDirectionFilterWithAvailable,
} from "utils/enclosure/filters";

const EnclosureFilterContext = createContext(null);

export const EnclosureFilterProvider = ({ children }) => {
    const modelId = useStateSelector(`model.modelId`);
    const componentsObject = useStateSelector(`model.modelData.components`) || {};

    const directionFilterOptions = useMemo(
        () => getAvailableDirectionFilterOptions(componentsObject),
        [componentsObject]
    );
    const availableDirectionValues = useMemo(
        () => getDefaultDirectionFilterForOptions(directionFilterOptions),
        [directionFilterOptions]
    );

    const [componentFilter, setComponentFilter] = useState(defaultComponentFilter);
    const [directionFilter, setDirectionFilterState] = useState(defaultDirectionFilter);

    const availableRef = useRef(availableDirectionValues);
    availableRef.current = availableDirectionValues;

    useEffect(() => {
        setDirectionFilterTrackingModelId(modelId);
        resetDirectionFilterAvailabilityTracking();
        setComponentFilter(defaultComponentFilter);
        setDirectionFilterState(
            normalizeDirectionFilterValue(
                syncDirectionFilterWithAvailable(defaultDirectionFilter, availableRef.current)
            )
        );
    }, [modelId]);

    useEffect(() => {
        setDirectionFilterState((saved) => {
            const synced = syncDirectionFilterWithAvailable(saved, availableDirectionValues);
            const normalized = normalizeDirectionFilterValue(synced);
            return isEqual(saved, normalized) ? saved : normalized;
        });
    }, [availableDirectionValues.join(",")]);

    const directionKeysByPath = useMemo(
        () => collectComponentDirectionKeysByPath(componentsObject),
        [componentsObject]
    );
    const previousDirectionKeysRef = useRef(null);

    useEffect(() => {
        previousDirectionKeysRef.current = null;
    }, [modelId]);

    // Keep components visible after their facing direction is edited (directly or via wall/door sync).
    useEffect(() => {
        const previous = previousDirectionKeysRef.current;
        previousDirectionKeysRef.current = directionKeysByPath;

        if (!previous) return;

        const changedDirections = [
            ...new Set(
                Object.entries(directionKeysByPath)
                    .filter(([path, key]) => path in previous && previous[path] !== key)
                    .map(([, key]) => key)
            ),
        ];

        if (!changedDirections.length) return;

        setDirectionFilterState((prev) => {
            const missing = changedDirections.filter((direction) => !prev.includes(direction));
            return missing.length ? normalizeDirectionFilterValue([...prev, ...missing]) : prev;
        });
    }, [directionKeysByPath]);

    const setDirectionFilter = useCallback((value) => {
        setDirectionFilterState((prev) => {
            const next = typeof value === "function" ? value(prev) : value;
            return normalizeDirectionFilterValue(next);
        });
    }, []);

    const clearFilters = useCallback(() => {
        setComponentFilter(defaultComponentFilter);
        setDirectionFilterState(
            normalizeDirectionFilterValue(
                syncDirectionFilterWithAvailable(defaultDirectionFilter, availableRef.current)
            )
        );
    }, []);

    /** Expand both filters so newly added components ({ type, componentData }) stay visible. */
    const revealComponents = useCallback((items = []) => {
        const types = [...new Set(items.map(({ type }) => type))];
        const directions = [
            ...new Set(items.map(({ type, componentData }) => getComponentDirectionKey(type, componentData))),
        ];

        setComponentFilter((prev) => {
            const missing = types.filter((type) => !prev.includes(type));
            return missing.length ? [...prev, ...missing] : prev;
        });
        setDirectionFilterState((prev) => {
            const missing = directions.filter((direction) => !prev.includes(direction));
            return missing.length ? normalizeDirectionFilterValue([...prev, ...missing]) : prev;
        });
    }, []);

    const isComponentFilterActive =
        componentFilter.length !== defaultComponentFilter.length ||
        !defaultComponentFilter.every((option) => componentFilter.includes(option));
    const isDirectionFilterActive = isDirectionFilterApplied(directionFilter, availableDirectionValues);

    const value = useMemo(
        () => ({
            componentFilter,
            directionFilter,
            availableDirectionValues,
            directionFilterOptions,
            setComponentFilter,
            setDirectionFilter,
            clearFilters,
            revealComponents,
            isComponentFilterActive,
            isDirectionFilterActive,
        }),
        [
            componentFilter,
            directionFilter,
            availableDirectionValues,
            directionFilterOptions,
            setDirectionFilter,
            clearFilters,
            revealComponents,
            isComponentFilterActive,
            isDirectionFilterActive,
        ]
    );

    return <EnclosureFilterContext.Provider value={value}>{children}</EnclosureFilterContext.Provider>;
};

export const useEnclosureFilter = () => {
    const context = useContext(EnclosureFilterContext);
    if (!context) {
        throw new Error("useEnclosureFilter must be used within EnclosureFilterProvider");
    }
    return context;
};
